import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getTerms,
  createTerm,
  updateTerm,
  deleteTerm,
  getTermById,
} from '../services/termService';
import Notification from '../components/Common/Notification';

export const TermContext = createContext();

export const TermProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllTerms = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTerms(authHeaders);
      setTerms(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching terms.';
      showNotification(errorMessage, true);
    }
  };

  const fetchTermById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTermById(id, authHeaders);
      setSelectedTerm(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching term with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addTerm = async (termData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newTerm = await createTerm(termData, authHeaders);
      setTerms(prevTerms => [...prevTerms, newTerm]);
      showNotification('Term added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding term.';
      showNotification(errorMessage, true);
    }
  };

  const editTerm = async (id, termData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedTerm = await updateTerm(id, termData, authHeaders);
      setTerms(prevTerms => prevTerms.map(term => (term.id === id ? updatedTerm : term)));
      showNotification('Term updated successfully!', false);
      return updatedTerm;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating term.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeTerm = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteTerm(id, authHeaders);
      setTerms(prevTerms => prevTerms.filter(term => term.id !== id));
      showNotification('Term removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing term.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllTerms();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <TermContext.Provider
      value={{
        terms,
        selectedTerm,
        fetchAllTerms,
        fetchTermById,
        addTerm,
        editTerm,
        removeTerm,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </TermContext.Provider>
  );
};

export const useTerm = () => useContext(TermContext);
